import React, { useState, useEffect } from "react";
import { Row, Col, Card, Alert, Spinner, Button, Form, Modal } from "react-bootstrap";
import {
  getPendaftaranById,
  getProfilPendampingSaya,
  updatePendaftaran,
  getProvinsi,
  getKabupaten,
  getKecamatan,
  getBanks,
  ajukanUlang,
  getPendampingFileUrl,
} from "../../services/api";
import { getStoredUser, setStoredUser } from "../../utils/authStorage";
import { useTheme } from "../../contexts/ThemeContext";
import {
  generateKartuPengenalImage,
  downloadDataUrl,
  printDataUrl,
  KARTU_FORMAT,
  getKartuFormatLabel,
  resolveNoRegister,
} from "../../utils/generateKartuPengenal";
import "./ProfilPendamping.css";
import "./Dashboard.css";

const getDocFileUrl = (folder, filename) => getPendampingFileUrl(folder, filename);

const isPdfFile = (filename) => /\.pdf$/i.test(filename || "");

const DocumentImagePreview = ({ folder, filename, alt, compact = false, srcUrl }) => {
  if (!filename) return null;
  if (isPdfFile(filename)) {
    return (
      <div
        className={`document-preview-image-wrap${compact ? " document-preview-image-wrap--compact" : ""}`}
      >
        <div className="text-center">
          <i className="fas fa-file-pdf text-danger fa-3x"></i>
          <p className="text-muted small mt-2 mb-0">Dokumen PDF</p>
        </div>
      </div>
    );
  }
  return (
    <div
      className={`document-preview-image-wrap${compact ? " document-preview-image-wrap--compact" : ""}`}
    >
      <img
        src={srcUrl || getDocFileUrl(folder, filename)}
        alt={alt}
        className="document-preview-image"
      />
    </div>
  );
};

const ProfilPendamping = () => {
  const { orgName, orgAlamat, orgTelepon, logoUrl, themeColor, themeDark, themeLight } = useTheme();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [ajukanUlangLoading, setAjukanUlangLoading] = useState(false);

  // Reference data states
  const [provinsiList, setProvinsiList] = useState([]);
  const [kabupatenList, setKabupatenList] = useState([]);
  const [kecamatanList, setKecamatanList] = useState([]);
  const [bankList, setBankList] = useState([]);

  // File upload states
  const [fileKTP, setFileKTP] = useState(null);
  const [fileIjazah, setFileIjazah] = useState(null);
  const [fileFoto, setFileFoto] = useState(null);

  const [showKartuModal, setShowKartuModal] = useState(false);
  const [kartuPreviewUrl, setKartuPreviewUrl] = useState("");
  const [kartuLoading, setKartuLoading] = useState(false);
  const [kartuFormat, setKartuFormat] = useState(KARTU_FORMAT.HORIZONTAL);

  useEffect(() => {
    const parsedUser = getStoredUser();
    if (parsedUser) {
      fetchProfile(parsedUser);
    }
    fetchProvinsi();
    fetchBanks();
  }, []);

  const fetchProfile = async (user) => {
    try {
      setLoading(true);
      setError("");

      let response;
      const role = user?.role;
      const isPendampingRole =
        user?.userid && (role === "R.45" || role === "R.47");

      if (isPendampingRole) {
        try {
          response = await getProfilPendampingSaya(user.userid);
        } catch (profilErr) {
          const fallbackId = user.id_pendamping || user.unik_id;
          if (fallbackId) {
            try {
              response = await getPendaftaranById(fallbackId);
            } catch {
              throw profilErr;
            }
          } else {
            throw profilErr;
          }
        }
        const resolvedId =
          response.data?.id_pendamping || response.data?.data?.id_pendamping;
        if (resolvedId) {
          const updatedUser = {
            ...user,
            unik_id: resolvedId,
            id_pendamping: resolvedId,
          };
          setStoredUser(updatedUser);
        }
      } else if (user?.unik_id) {
        response = await getPendaftaranById(user.unik_id);
      } else {
        setError("Data profil tidak ditemukan");
        return;
      }

      if (response.data.success) {
        setProfile(response.data.data);
        setFormData(response.data.data);
      } else {
        setError("Data profil tidak ditemukan");
      }
    } catch (err) {
      console.error("Error fetching profile:", err);
      setError(
        err.response?.data?.message || "Gagal memuat data profil",
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchProvinsi = async () => {
    try {
      const response = await getProvinsi();
      if (response.data.success) {
        setProvinsiList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching provinsi:", error);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await getBanks();
      if (response.data.success) {
        setBankList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching banks:", error);
    }
  };

  const fetchKabupaten = async (kodeprovinsi) => {
    try {
      const response = await getKabupaten(kodeprovinsi);
      if (response.data.success) {
        setKabupatenList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching kabupaten:", error);
    }
  };

  const fetchKecamatan = async (kodekabupaten) => {
    try {
      const response = await getKecamatan(kodekabupaten);
      if (response.data.success) {
        setKecamatanList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching kecamatan:", error);
    }
  };

  const handleEditClick = async () => {
    setIsEditMode(true);
    setError("");
    setSuccessMessage("");

    // Load kabupaten and kecamatan if provinsi and kabupaten are set
    if (formData.provinsi) {
      await fetchKabupaten(formData.provinsi);
    }
    if (formData.kabupaten) {
      await fetchKecamatan(formData.kabupaten);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setFormData(profile); // Reset to original data
    setFileKTP(null);
    setFileIjazah(null);
    setFileFoto(null);
    setError("");
    setSuccessMessage("");
  };

  const handleAjukanUlang = async () => {
    try {
      setAjukanUlangLoading(true);
      setError("");
      setSuccessMessage("");
      const response = await ajukanUlang(profile.id_pendamping);
      if (response.data.success) {
        setSuccessMessage("Pengajuan ulang berhasil dikirim. Status Anda kembali menjadi Calon Peserta.");
        const parsedUser = getStoredUser();
        if (parsedUser) {
          fetchProfile(parsedUser);
        }
      } else {
        setError(response.data.message || "Gagal mengajukan ulang.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Gagal menghubungi server.");
    } finally {
      setAjukanUlangLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleProvinsiChange = async (e) => {
    const kodeprovinsi = e.target.value;
    setFormData({
      ...formData,
      provinsi: kodeprovinsi,
      kabupaten: "",
      kecamatan: "",
    });
    setKabupatenList([]);
    setKecamatanList([]);
    if (kodeprovinsi) {
      await fetchKabupaten(kodeprovinsi);
    }
  };

  const handleKabupatenChange = async (e) => {
    const kodekabupaten = e.target.value;
    setFormData({
      ...formData,
      kabupaten: kodekabupaten,
      kecamatan: "",
    });
    setKecamatanList([]);
    if (kodekabupaten) {
      await fetchKecamatan(kodekabupaten);
    }
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (type === "ktp") {
        setFileKTP(file);
      } else if (type === "ijazah") {
        setFileIjazah(file);
      } else if (type === "foto") {
        setFileFoto(file);
      }
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccessMessage("");

      const formDataToSubmit = new FormData();

      // Only append fields that should be updated
      const fieldsToUpdate = [
        "nama",
        "alamat",
        "provinsi",
        "kabupaten",
        "kecamatan",
        "kelurahan",
        "kode_pos",
        "tempat_lahir",
        "tgl_lahir",
        "no_hp",
        "email",
        "nik",
        "pendidikan",
        "programstudi",
        "universitas",
        "pekerjaan",
        "pekerjaan_lain",
        "kode_bank",
        "no_rekening",
        "nama_rekening",
        "alasan",
      ];

      fieldsToUpdate.forEach((key) => {
        if (formData[key] !== null && formData[key] !== undefined) {
          // Format date properly
          if (key === "tgl_lahir" && formData[key]) {
            const dateValue = formData[key].split("T")[0];
            formDataToSubmit.append(key, dateValue);
          } else {
            formDataToSubmit.append(key, formData[key]);
          }
        }
      });

      // Append files if selected
      if (fileFoto) {
        formDataToSubmit.append("filefoto", fileFoto);
      }
      if (fileKTP) {
        formDataToSubmit.append("fotoktp", fileKTP);
      }
      if (fileIjazah) {
        formDataToSubmit.append("fotoijazah", fileIjazah);
      }

      console.log("Submitting update for ID:", profile.id_pendamping);
      console.log("Files to upload:", {
        Foto: fileFoto?.name,
        KTP: fileKTP?.name,
        Ijazah: fileIjazah?.name,
      });

      const response = await updatePendaftaran(
        profile.id_pendamping,
        formDataToSubmit,
      );

      if (response.data.success) {
        setSuccessMessage("Data berhasil diupdate!");
        setIsEditMode(false);
        setFileKTP(null);
        setFileIjazah(null);
        setFileFoto(null);
        const parsedUser = getStoredUser();
        if (parsedUser) {
          fetchProfile(parsedUser);
        }
      }
    } catch (err) {
      console.error("Error updating profile:", err);
      setError(err.response?.data?.message || "Gagal mengupdate data profil");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("id-ID", options);
  };

  const buildAbsoluteUrl = (path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    const origin =
      typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
  };

  const resolveFotoUrl = (p) => {
    if (!p?.filefoto) return "";
    return getPendampingFileUrl("foto", p.filefoto);
  };

  const resolveFotoUrlForKartu = (p) => resolveFotoUrl(p);

  const resolveFotoHref = (p) => {
    if (!p?.filefoto) return null;
    return getPendampingFileUrl("foto", p.filefoto);
  };

  const buildKartuOpts = () => {
    return {
      nama: profile.nama,
      nik: profile.nik,
      status: profile.status,
      email: profile.email,
      noHp: profile.no_hp,
      tglLahir: profile.tgl_lahir,
      kodeReferral: profile.kode_referral,
      noRegister: profile.no_register,
      idPendamping: profile.id_pendamping,
      fotoUrl: resolveFotoUrlForKartu(profile),
      logoUrl: logoUrl ? buildAbsoluteUrl(logoUrl) : "",
      orgName: orgName || "LP3H",
      orgAlamat,
      orgTelepon,
      primaryColor: themeColor,
      primaryDark: themeDark,
      primaryLight: themeLight,
      kabupatenNama: profile.namakabupaten,
      provinsiNama: profile.namaprovinsi,
    };
  };

  const handleCetakKartu = async (format = KARTU_FORMAT.HORIZONTAL) => {
    if (!profile) return;
    setKartuFormat(format);
    setKartuLoading(true);
    setError("");
    try {
      const dataUrl = await generateKartuPengenalImage(buildKartuOpts(), format);
      setKartuPreviewUrl(dataUrl);
      setShowKartuModal(true);
    } catch (err) {
      console.error("Generate kartu error:", err);
      setError(err.message || "Gagal membuat kartu pengenal.");
    } finally {
      setKartuLoading(false);
    }
  };

  const handleSwitchKartuFormat = async (format) => {
    if (!profile || format === kartuFormat) return;
    setKartuFormat(format);
    setKartuLoading(true);
    try {
      const dataUrl = await generateKartuPengenalImage(buildKartuOpts(), format);
      setKartuPreviewUrl(dataUrl);
    } catch (err) {
      setError(err.message || "Gagal mengubah format kartu.");
    } finally {
      setKartuLoading(false);
    }
  };

  const handleDownloadKartu = () => {
    if (!kartuPreviewUrl || !profile) return;
    const safeNik = (profile.nik || "peserta").replace(/\D/g, "") || "peserta";
    const suffix = kartuFormat === KARTU_FORMAT.VERTICAL ? "gantungan" : "landscape";
    downloadDataUrl(kartuPreviewUrl, `kartu-pengenal-${suffix}-${safeNik}.png`);
  };

  const handlePrintKartu = () => {
    if (!kartuPreviewUrl) return;
    try {
      printDataUrl(
        kartuPreviewUrl,
        `Kartu Pengenal - ${profile?.nama || ""}`,
        kartuFormat
      );
    } catch (err) {
      setError(err.message || "Gagal membuka dialog cetak.");
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "CALON PESERTA":
        return "badge bg-info";
      case "AKTIF":
        return "badge bg-success";
      case "DISETUJUI":
        return "badge bg-success";
      case "NON AKTIF":
        return "badge bg-secondary";
      case "DITOLAK":
        return "badge bg-danger";
      default:
        return "badge bg-secondary";
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Memuat data profil...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
      </Alert>
    );
  }

  if (!profile) {
    return (
      <Alert variant="warning">
        <i className="fas fa-info-circle me-2"></i>
        Data profil tidak ditemukan
      </Alert>
    );
  }

  return (
    <div className="profil-pendamping-page">
      {successMessage && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSuccessMessage("")}
        >
          <i className="fas fa-check-circle me-2"></i>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      <div
        className="page-header-banner"
        style={{
          background:
            "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 50%, var(--primary-dark) 100%)",
        }}
      >
        <div className="page-header-content">
          <div className="page-header-left">
            <div className="page-header-icon">
              <i className="fas fa-user-circle"></i>
            </div>
            <div>
              <h1 className="page-header-title">Profil Saya</h1>
              <p className="page-header-subtitle">
                {isEditMode
                  ? "Mode Edit - Ubah data profil Anda"
                  : "Informasi lengkap data pendaftaran Anda"}
              </p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {!isEditMode ? (
              <>
                <Button
                  variant="light"
                  onClick={() => handleCetakKartu(KARTU_FORMAT.HORIZONTAL)}
                  disabled={kartuLoading}
                  className="text-primary fw-semibold"
                >
                  {kartuLoading && kartuFormat === KARTU_FORMAT.HORIZONTAL ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Membuat kartu...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-id-card me-2"></i>
                      Kartu Landscape
                    </>
                  )}
                </Button>
                <Button
                  variant="outline-light"
                  onClick={() => handleCetakKartu(KARTU_FORMAT.VERTICAL)}
                  disabled={kartuLoading}
                >
                  {kartuLoading && kartuFormat === KARTU_FORMAT.VERTICAL ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Membuat kartu...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-id-badge me-2"></i>
                      Kartu Gantungan
                    </>
                  )}
                </Button>
                <Button variant="outline-light" onClick={handleEditClick}>
                  <i className="fas fa-edit me-2"></i>
                  Edit Profil
                </Button>
              </>
            ) : (
              <div className="d-flex gap-2">
                <Button
                  variant="outline-light"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        className="me-2"
                      />
                      Menyimpan...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Simpan
                    </>
                  )}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  <i className="fas fa-times me-2"></i>
                  Batal
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status Card */}
      <Card className="mb-4 shadow-sm status-card">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3">
              {profile.filefoto ? (
                <img
                  src={resolveFotoHref(profile) || getDocFileUrl("foto", profile.filefoto)}
                  alt="Foto profil"
                  className="profil-foto-thumb rounded-circle border"
                  style={{ width: 72, height: 72, objectFit: "cover" }}
                />
              ) : (
                <div
                  className="rounded-circle border bg-light d-flex align-items-center justify-content-center text-muted"
                  style={{ width: 72, height: 72 }}
                >
                  <i className="fas fa-user fa-2x"></i>
                </div>
              )}
              <div>
              <h4 className="mb-2 fw-bold">{profile.nama}</h4>
              <p className="text-muted mb-0">
                <i className="fas fa-envelope me-2"></i>
                {profile.email}
              </p>
              </div>
            </div>
            <div>
              <span className={getStatusBadgeClass(profile.status)}>
                {profile.status || "CALON PESERTA"}
              </span>
            </div>
          </div>
          {profile.status === "DITOLAK" && profile.catatan && (
            <Alert variant="danger" className="mt-3 mb-0">
              <div className="fw-semibold mb-1">
                <i className="fas fa-times-circle me-2"></i>Alasan Penolakan:
              </div>
              <div>{profile.catatan}</div>
              <div className="mt-2">
                <Button
                  variant="warning"
                  size="sm"
                  onClick={handleAjukanUlang}
                  disabled={ajukanUlangLoading}
                >
                  {ajukanUlangLoading ? (
                    <><Spinner animation="border" size="sm" className="me-2" />Memproses...</>
                  ) : (
                    <><i className="fas fa-redo me-2"></i>AJUKAN ULANG</>
                  )}
                </Button>
              </div>
            </Alert>
          )}
        </Card.Body>
      </Card>

      <Row>
        {/* Data Pribadi */}
        <Col lg={6} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <i className="fas fa-user me-2"></i>
                Data Pribadi
              </h5>
            </Card.Header>
            <Card.Body>
              {!isEditMode ? (
                <div className="info-group">
                  <div className="info-item">
                    <label>Nama Lengkap</label>
                    <p>{profile.nama || "-"}</p>
                  </div>
                  <div className="info-item">
                    <label>NIK</label>
                    <p>{profile.nik || "-"}</p>
                  </div>
                  <div className="info-item">
                    <label>No. Register</label>
                    <p>{resolveNoRegister(profile.status, profile.no_register)}</p>
                  </div>
                  <div className="info-item">
                    <label>Tempat, Tanggal Lahir</label>
                    <p>
                      {profile.tempat_lahir || "-"},{" "}
                      {formatDate(profile.tgl_lahir)}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>No. HP</label>
                    <p>{profile.no_hp || "-"}</p>
                  </div>
                  <div className="info-item">
                    <label>Email</label>
                    <p>{profile.email || "-"}</p>
                  </div>
                </div>
              ) : (
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Nama Lengkap</Form.Label>
                    <Form.Control
                      type="text"
                      name="nama"
                      value={formData.nama || ""}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>NIK</Form.Label>
                    <Form.Control
                      type="text"
                      name="nik"
                      value={formData.nik || ""}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Tempat Lahir</Form.Label>
                    <Form.Control
                      type="text"
                      name="tempat_lahir"
                      value={formData.tempat_lahir || ""}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Tanggal Lahir</Form.Label>
                    <Form.Control
                      type="date"
                      name="tgl_lahir"
                      value={
                        formData.tgl_lahir
                          ? formData.tgl_lahir.split("T")[0]
                          : ""
                      }
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>No. HP</Form.Label>
                    <Form.Control
                      type="text"
                      name="no_hp"
                      value={formData.no_hp || ""}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email || ""}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Data Alamat */}
        <Col lg={6} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header className="bg-success text-white">
              <h5 className="mb-0">
                <i className="fas fa-map-marker-alt me-2"></i>
                Alamat
              </h5>
            </Card.Header>
            <Card.Body>
              {!isEditMode ? (
                <div className="info-group">
                  <div className="info-item">
                    <label>Alamat Lengkap</label>
                    <p>{profile.alamat || "-"}</p>
                  </div>
                  <div className="info-item">
                    <label>Kelurahan</label>
                    <p>{profile.kelurahan || "-"}</p>
                  </div>
                  <div className="info-item">
                    <label>Kecamatan</label>
                    <p>{profile.namakecamatan || "-"}</p>
                  </div>
                  <div className="info-item">
                    <label>Kabupaten/Kota</label>
                    <p>{profile.namakabupaten || "-"}</p>
                  </div>
                  <div className="info-item">
                    <label>Provinsi</label>
                    <p>{profile.namaprovinsi || "-"}</p>
                  </div>
                  <div className="info-item">
                    <label>Kode Pos</label>
                    <p>{profile.kode_pos || "-"}</p>
                  </div>
                </div>
              ) : (
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Alamat Lengkap</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="alamat"
                      value={formData.alamat || ""}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Provinsi</Form.Label>
                    <Form.Select
                      name="provinsi"
                      value={formData.provinsi || ""}
                      onChange={handleProvinsiChange}
                      required
                    >
                      <option value="">Pilih Provinsi</option>
                      {provinsiList.map((prov) => (
                        <option
                          key={prov.kodeprovinsi}
                          value={prov.kodeprovinsi}
                        >
                          {prov.namaprovinsi}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Kabupaten/Kota</Form.Label>
                    <Form.Select
                      name="kabupaten"
                      value={formData.kabupaten || ""}
                      onChange={handleKabupatenChange}
                      required
                      disabled={!formData.provinsi}
                    >
                      <option value="">Pilih Kabupaten/Kota</option>
                      {kabupatenList.map((kab) => (
                        <option
                          key={kab.kodekabupaten}
                          value={kab.kodekabupaten}
                        >
                          {kab.namakabupaten}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Kecamatan</Form.Label>
                    <Form.Select
                      name="kecamatan"
                      value={formData.kecamatan || ""}
                      onChange={handleChange}
                      required
                      disabled={!formData.kabupaten}
                    >
                      <option value="">Pilih Kecamatan</option>
                      {kecamatanList.map((kec) => (
                        <option
                          key={kec.kodekecamatan}
                          value={kec.kodekecamatan}
                        >
                          {kec.namakecamatan}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Kelurahan</Form.Label>
                    <Form.Control
                      type="text"
                      name="kelurahan"
                      value={formData.kelurahan || ""}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Kode Pos</Form.Label>
                    <Form.Control
                      type="text"
                      name="kode_pos"
                      value={formData.kode_pos || ""}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Data Pendidikan */}
        <Col lg={6} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header className="bg-info text-white">
              <h5 className="mb-0">
                <i className="fas fa-graduation-cap me-2"></i>
                Pendidikan
              </h5>
            </Card.Header>
            <Card.Body>
              {!isEditMode ? (
                <div className="info-group">
                  <div className="info-item">
                    <label>Pendidikan Terakhir</label>
                    <p>{profile.pendidikan || "-"}</p>
                  </div>
                  <div className="info-item">
                    <label>Program Studi</label>
                    <p>{profile.programstudi || "-"}</p>
                  </div>
                  <div className="info-item">
                    <label>Universitas/Institusi</label>
                    <p>{profile.universitas || "-"}</p>
                  </div>
                </div>
              ) : (
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Pendidikan Terakhir</Form.Label>
                    <Form.Select
                      name="pendidikan"
                      value={formData.pendidikan || ""}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Pilih Pendidikan</option>
                      <option value="S3">S3</option>
                      <option value="S2">S2</option>
                      <option value="S1">S1</option>
                      <option value="D3">D3</option>
                      <option value="SMA/SMK">SMA/SMK</option>
                      <option value="SMP">SMP</option>
                      <option value="SD">SD</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Program Studi</Form.Label>
                    <Form.Control
                      type="text"
                      name="programstudi"
                      value={formData.programstudi || ""}
                      onChange={handleChange}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Universitas/Institusi</Form.Label>
                    <Form.Control
                      type="text"
                      name="universitas"
                      value={formData.universitas || ""}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Data Pekerjaan */}
        <Col lg={6} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header className="bg-warning text-dark">
              <h5 className="mb-0">
                <i className="fas fa-briefcase me-2"></i>
                Pekerjaan
              </h5>
            </Card.Header>
            <Card.Body>
              {!isEditMode ? (
                <div className="info-group">
                  <div className="info-item">
                    <label>Pekerjaan</label>
                    <p>{profile.pekerjaan || "-"}</p>
                  </div>
                  {profile.pekerjaan_lain && (
                    <div className="info-item">
                      <label>Pekerjaan Lainnya</label>
                      <p>{profile.pekerjaan_lain}</p>
                    </div>
                  )}
                </div>
              ) : (
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Pekerjaan</Form.Label>
                    <Form.Select
                      name="pekerjaan"
                      value={formData.pekerjaan || ""}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Pilih Pekerjaan</option>
                      <option value="Pegawai Swasta">Pegawai Swasta</option>
                      <option value="PNS">PNS</option>
                      <option value="Wiraswasta">Wiraswasta</option>
                      <option value="Ibu Rumah Tangga">Ibu Rumah Tangga</option>
                      <option value="Mahasiswa">Mahasiswa</option>
                      <option value="Lainnya">Lainnya</option>
                    </Form.Select>
                  </Form.Group>
                  {formData.pekerjaan === "Lainnya" && (
                    <Form.Group className="mb-3">
                      <Form.Label>Pekerjaan Lainnya</Form.Label>
                      <Form.Control
                        type="text"
                        name="pekerjaan_lain"
                        value={formData.pekerjaan_lain || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  )}
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Data Rekening Bank */}
        <Col lg={6} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header className="bg-secondary text-white">
              <h5 className="mb-0">
                <i className="fas fa-university me-2"></i>
                Data Rekening Bank
              </h5>
            </Card.Header>
            <Card.Body>
              {!isEditMode ? (
                <div className="info-group">
                  <div className="info-item">
                    <label>Bank</label>
                    <p>{profile.kode_bank || "-"}</p>
                  </div>
                  <div className="info-item">
                    <label>Nomor Rekening</label>
                    <p>{profile.no_rekening || "-"}</p>
                  </div>
                  <div className="info-item">
                    <label>Nama Pemilik Rekening</label>
                    <p>{profile.nama_rekening || "-"}</p>
                  </div>
                </div>
              ) : (
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Bank</Form.Label>
                    <Form.Select
                      name="kode_bank"
                      value={formData.kode_bank || ""}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Pilih Bank</option>
                      {bankList.map((bank) => (
                        <option key={bank.kode_bank} value={bank.kode_bank}>
                          {bank.nama_bank}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Nomor Rekening</Form.Label>
                    <Form.Control
                      type="text"
                      name="no_rekening"
                      value={formData.no_rekening || ""}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Nama Pemilik Rekening</Form.Label>
                    <Form.Control
                      type="text"
                      name="nama_rekening"
                      value={formData.nama_rekening || ""}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Informasi Tambahan */}
        <Col lg={6} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header className="bg-dark text-white">
              <h5 className="mb-0">
                <i className="fas fa-info-circle me-2"></i>
                Informasi Tambahan
              </h5>
            </Card.Header>
            <Card.Body>
              {!isEditMode ? (
                <div className="info-group">
                  {profile.kode_referral && (
                    <div className="info-item">
                      <label>Kode Referral Anda</label>
                      <p className="font-monospace fw-bold text-primary">
                        {profile.kode_referral}
                      </p>
                    </div>
                  )}
                  {profile.referral && (
                    <div className="info-item">
                      <label>Referral Dari</label>
                      <p>
                        {profile.nama_referral
                          ? `${profile.referral} - ${profile.nama_referral}`
                          : "-"}
                      </p>
                    </div>
                  )}
                  <div className="info-item">
                    <label>Alasan Menjadi P3H</label>
                    <p>{profile.alasan || "-"}</p>
                  </div>
                  <div className="info-item">
                    <label>Tanggal Pendaftaran</label>
                    <p>{formatDate(profile.created_on)}</p>
                  </div>
                </div>
              ) : (
                <Form>
                  <div className="info-group">
                    {profile.kode_referral && (
                      <div className="info-item">
                        <label>Kode Referral Anda</label>
                        <p className="font-monospace fw-bold text-primary">
                          {profile.kode_referral}
                        </p>
                      </div>
                    )}
                    {profile.referral && (
                      <div className="info-item">
                        <label>Referral Dari</label>
                        <p>
                          {profile.nama_referral
                            ? `${profile.referral} - ${profile.nama_referral}`
                            : "-"}
                        </p>
                      </div>
                    )}
                  </div>
                  <Form.Group className="mb-3">
                    <Form.Label>Alasan Menjadi P3H</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="alasan"
                      value={formData.alasan || ""}
                      onChange={handleChange}
                    />
                  </Form.Group>
                  <div className="info-item">
                    <label>Tanggal Pendaftaran</label>
                    <p>{formatDate(profile.created_on)}</p>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Documents */}
      <Card className="shadow-sm">
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="fas fa-file-alt me-2"></i>
            Dokumen
          </h5>
          {isEditMode && (
            <small className="text-white">
              <i className="fas fa-info-circle me-1"></i>
              Upload file baru jika ingin mengganti dokumen
            </small>
          )}
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4} className="mb-3">
              <label className="fw-bold d-block mb-3">
                <i className="fas fa-user me-2"></i>
                Foto Diri
              </label>
              {!isEditMode ? (
                <>
                  {profile.filefoto ? (
                    <div className="document-preview">
                      <DocumentImagePreview
                        folder="foto"
                        filename={profile.filefoto}
                        alt="Foto diri"
                        srcUrl={resolveFotoHref(profile)}
                      />
                      <p className="mb-3 text-muted small">{profile.filefoto}</p>
                      <Button
                        variant="secondary"
                        size="sm"
                        href={resolveFotoHref(profile)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-100"
                      >
                        <i className="fas fa-download me-2"></i>
                        Download Foto
                      </Button>
                    </div>
                  ) : (
                    <Alert variant="info" className="mb-0">
                      <i className="fas fa-info-circle me-2"></i>
                      Foto diri belum diupload. Klik <strong>Edit Profil</strong>{" "}
                      untuk menambahkan.
                    </Alert>
                  )}
                </>
              ) : (
                <div>
                  {profile.filefoto && !fileFoto && (
                    <div className="mb-3">
                      <DocumentImagePreview
                        folder="foto"
                        filename={profile.filefoto}
                        alt="Foto saat ini"
                        compact
                        srcUrl={resolveFotoHref(profile)}
                      />
                      <Alert variant="info" className="mb-2 py-2">
                        <small>
                          <i className="fas fa-file-check me-2"></i>
                          File saat ini: {profile.filefoto}
                        </small>
                      </Alert>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        href={resolveFotoHref(profile)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mb-2"
                      >
                        <i className="fas fa-eye me-2"></i>
                        Lihat File
                      </Button>
                    </div>
                  )}
                  <Form.Group>
                    <Form.Control
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      onChange={(e) => handleFileChange(e, "foto")}
                    />
                    <Form.Text className="text-muted">
                      {fileFoto ? (
                        <span className="text-success">
                          <i className="fas fa-check me-1"></i>
                          File baru dipilih: {fileFoto.name}
                        </span>
                      ) : (
                        <span>
                          Upload foto diri (JPG/PNG, max 5MB)
                          {profile.filefoto &&
                            " — biarkan kosong jika tidak ingin mengubah"}
                        </span>
                      )}
                    </Form.Text>
                  </Form.Group>
                </div>
              )}
            </Col>
            <Col md={4} className="mb-3">
              <label className="fw-bold d-block mb-3">
                <i className="fas fa-id-card me-2"></i>
                Foto KTP
              </label>
              {!isEditMode ? (
                <>
                  {profile.fotoktp ? (
                    <div className="document-preview">
                      <DocumentImagePreview
                        folder="ktp"
                        filename={profile.fotoktp}
                        alt="Foto KTP"
                      />
                      <p className="mb-3 text-muted small">{profile.fotoktp}</p>
                      <Button
                        variant="primary"
                        size="sm"
                        href={getDocFileUrl("ktp", profile.fotoktp)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-100"
                      >
                        <i className="fas fa-download me-2"></i>
                        Download KTP
                      </Button>
                    </div>
                  ) : (
                    <Alert variant="warning" className="mb-0">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Dokumen KTP belum diupload
                    </Alert>
                  )}
                </>
              ) : (
                <div>
                  {profile.fotoktp && !fileKTP && (
                    <div className="mb-3">
                      <DocumentImagePreview
                        folder="ktp"
                        filename={profile.fotoktp}
                        alt="KTP saat ini"
                        compact
                      />
                      <Alert variant="info" className="mb-2 py-2">
                        <small>
                          <i className="fas fa-file-check me-2"></i>
                          File saat ini: {profile.fotoktp}
                        </small>
                      </Alert>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        href={getDocFileUrl("ktp", profile.fotoktp)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mb-2"
                      >
                        <i className="fas fa-eye me-2"></i>
                        Lihat File
                      </Button>
                    </div>
                  )}
                  <Form.Group>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, "ktp")}
                    />
                    <Form.Text className="text-muted">
                      {fileKTP ? (
                        <span className="text-success">
                          <i className="fas fa-check me-1"></i>
                          File baru dipilih: {fileKTP.name}
                        </span>
                      ) : (
                        <span>
                          Upload foto KTP (format: JPG, PNG, max 5MB)
                          {profile.fotoktp &&
                            " - Biarkan kosong jika tidak ingin mengubah"}
                        </span>
                      )}
                    </Form.Text>
                  </Form.Group>
                </div>
              )}
            </Col>
            <Col md={4} className="mb-3">
              <label className="fw-bold d-block mb-3">
                <i className="fas fa-graduation-cap me-2"></i>
                Foto Ijazah
              </label>
              {!isEditMode ? (
                <>
                  {profile.fotoijazah ? (
                    <div className="document-preview">
                      <DocumentImagePreview
                        folder="ijazah"
                        filename={profile.fotoijazah}
                        alt="Foto ijazah"
                      />
                      <p className="mb-3 text-muted small">
                        {profile.fotoijazah}
                      </p>
                      <Button
                        variant="success"
                        size="sm"
                        href={getDocFileUrl("ijazah", profile.fotoijazah)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-100"
                      >
                        <i className="fas fa-download me-2"></i>
                        Download Ijazah
                      </Button>
                    </div>
                  ) : (
                    <Alert variant="warning" className="mb-0">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Dokumen Ijazah belum diupload
                    </Alert>
                  )}
                </>
              ) : (
                <div>
                  {profile.fotoijazah && !fileIjazah && (
                    <div className="mb-3">
                      <DocumentImagePreview
                        folder="ijazah"
                        filename={profile.fotoijazah}
                        alt="Ijazah saat ini"
                        compact
                      />
                      <Alert variant="info" className="mb-2 py-2">
                        <small>
                          <i className="fas fa-file-check me-2"></i>
                          File saat ini: {profile.fotoijazah}
                        </small>
                      </Alert>
                      <Button
                        variant="outline-success"
                        size="sm"
                        href={getDocFileUrl("ijazah", profile.fotoijazah)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mb-2"
                      >
                        <i className="fas fa-eye me-2"></i>
                        Lihat File
                      </Button>
                    </div>
                  )}
                  <Form.Group>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, "ijazah")}
                    />
                    <Form.Text className="text-muted">
                      {fileIjazah ? (
                        <span className="text-success">
                          <i className="fas fa-check me-1"></i>
                          File baru dipilih: {fileIjazah.name}
                        </span>
                      ) : (
                        <span>
                          Upload foto Ijazah (format: JPG, PNG, max 5MB)
                          {profile.fotoijazah &&
                            " - Biarkan kosong jika tidak ingin mengubah"}
                        </span>
                      )}
                    </Form.Text>
                  </Form.Group>
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Modal
        show={showKartuModal}
        onHide={() => setShowKartuModal(false)}
        size="lg"
        centered
        className={`kartu-pengenal-modal${
          kartuFormat === KARTU_FORMAT.VERTICAL ? " kartu-pengenal-modal--vertical" : ""
        }`}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-id-card me-2"></i>
            Kartu Pengenal — {getKartuFormatLabel(kartuFormat)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {!profile.filefoto && (
            <Alert variant="warning" className="text-start">
              Foto diri belum diupload. Kartu tetap bisa dicetak, tetapi
              disarankan upload foto di bagian Dokumen terlebih dahulu.
            </Alert>
          )}
          <div className="d-flex justify-content-center gap-2 mb-3 flex-wrap">
            <Button
              size="sm"
              variant={
                kartuFormat === KARTU_FORMAT.HORIZONTAL ? "primary" : "outline-primary"
              }
              onClick={() => handleSwitchKartuFormat(KARTU_FORMAT.HORIZONTAL)}
              disabled={kartuLoading}
            >
              Landscape
            </Button>
            <Button
              size="sm"
              variant={
                kartuFormat === KARTU_FORMAT.VERTICAL ? "primary" : "outline-primary"
              }
              onClick={() => handleSwitchKartuFormat(KARTU_FORMAT.VERTICAL)}
              disabled={kartuLoading}
            >
              Gantungan Leher
            </Button>
          </div>
          {kartuLoading ? (
            <Spinner animation="border" />
          ) : kartuPreviewUrl ? (
            <div className="kartu-pengenal-preview-wrap">
              <img
                src={kartuPreviewUrl}
                alt="Preview kartu pengenal"
                className="kartu-pengenal-preview-img"
              />
            </div>
          ) : null}
          <p className="text-muted small mt-3 mb-0">
            {kartuFormat === KARTU_FORMAT.VERTICAL
              ? "Ukuran gantungan leher (54 × 86 mm). Cocok untuk tali ID / lanyard."
              : "Ukuran standar kartu ID landscape (85,6 × 54 mm). Gunakan kertas foto atau PVC card saat cetak."}
          </p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center gap-2 flex-wrap">
          <Button variant="primary" onClick={handleDownloadKartu} disabled={!kartuPreviewUrl}>
            <i className="fas fa-download me-2"></i>
            Download PNG
          </Button>
          <Button variant="outline-primary" onClick={handlePrintKartu} disabled={!kartuPreviewUrl}>
            <i className="fas fa-print me-2"></i>
            Cetak
          </Button>
          <Button variant="secondary" onClick={() => setShowKartuModal(false)}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProfilPendamping;
